import { formatApiErrorDetail } from '@/shared/utils/formatApiErrorDetail';
import { normalizeJobPhase } from '@/features/datasets/utils/generationPhaseLabels';

/**
 * Shared helpers for showing upload (client-side) and processing (server-side) progress
 * in the global FloatingProgressBar.
 *
 * Convention:
 * - Overall progress bar: 0..50% = upload, 50..100% = processing
 * - Server-side processing events still send 0..100; the global WS handler maps them to 50..100 when phase fields exist.
 */

/** Matches backend `make_clone_dataset_name` base truncation (50 total − 10 suffix). */
export const CLONE_SOURCE_BASE_MAX_LEN = 40;

/**
 * Truncate a library / raw dataset name to the base segment used inside clone keys
 * (same as multi-objective upload `datasetName.slice(0, 40)`).
 */
export function librarySourceBaseForCloneGroup(sourceDatasetName) {
  const s = String(sourceDatasetName || '');
  if (s.length <= CLONE_SOURCE_BASE_MAX_LEN) return s;
  return s.slice(0, CLONE_SOURCE_BASE_MAX_LEN);
}

/**
 * Predicted per-usecase clone dataset name (must stay aligned with `make_clone_dataset_name`).
 */
export function predictedCloneNameForUsecase(sourceBase, usecaseId) {
  const id = String(usecaseId || '');
  return `${sourceBase}__t_${id.slice(-6)}`;
}

/**
 * Merge one child's processing progress into a coalesced parent row (multi-objective upload or library clone).
 * Mirrors `globalState.jsx` `updateProgress` when `parentKey !== event.datasetName`.
 *
 * @param {Record<string, unknown>} existing - Parent `progressStatus[groupKey]` entry
 * @param {{ childCloneName: string, childProgress: number, childIsError?: boolean }} update
 * @param {number} childCount - Expected number of children (from upload group registration)
 */
export function mergeCoalescedGroupChildIntoParent(existing, { childCloneName, childProgress, childIsError = false }, childCount) {
  const nextChildProgresses = {
    ...(existing?.childProgresses || {}),
    [childCloneName]: typeof childProgress === 'number' ? childProgress : 0,
  };
  const nextChildErrors = {
    ...(existing?.childErrors || {}),
    [childCloneName]: !!childIsError,
  };
  const reportedValues = Object.values(nextChildProgresses);
  const count = childCount || reportedValues.length;
  const filled = [
    ...reportedValues,
    ...Array(Math.max(0, count - reportedValues.length)).fill(0),
  ];
  const processingProgress = filled.length > 0 ? Math.min(...filled) : 0;
  const aggregatedIsError = Object.values(nextChildErrors).some(Boolean);
  const allReported = count > 0 && reportedValues.length >= count;
  const aggregatedIsCompleted =
    allReported && reportedValues.every((p) => p >= 100) && !aggregatedIsError;
  return {
    nextChildProgresses,
    nextChildErrors,
    processingProgress,
    aggregatedIsCompleted,
    aggregatedIsError,
  };
}

/**
 * Apply coalesced child aggregation to parent fields (progress bar, phase, message).
 */
export function applyCoalescedProgressPatchToParent(existing, mergeResult) {
  const hasClientUploadPhase =
    existing?.phase === 'uploading' ||
    existing?.phase === 'processing' ||
    typeof existing?.uploadProgress === 'number';
  const { processingProgress, aggregatedIsCompleted, aggregatedIsError } = mergeResult;
  const processingDone = processingProgress >= 100 || aggregatedIsCompleted;
  const overallProgress = hasClientUploadPhase
    ? Math.max(50, Math.min(100, 50 + (processingProgress / 100) * 50))
    : processingProgress;
  const nextMessage = (() => {
    if (aggregatedIsError) return existing?.message || 'Processing failed';
    if (aggregatedIsCompleted) return 'Processing complete';
    if (hasClientUploadPhase) return 'Processing dataset';
    return existing?.message || 'Processing dataset';
  })();
  const nextPhase = (() => {
    if (!hasClientUploadPhase) return existing?.phase;
    if (processingDone || aggregatedIsCompleted) return 'completed';
    return 'processing';
  })();
  return {
    ...existing,
    progress: overallProgress,
    isCompleted: aggregatedIsCompleted,
    isError: aggregatedIsError,
    uploadProgress: hasClientUploadPhase ? (existing?.uploadProgress ?? 100) : existing?.uploadProgress,
    processingProgress: hasClientUploadPhase ? processingProgress : existing?.processingProgress,
    phase: nextPhase,
    message: nextMessage,
    childProgresses: mergeResult.nextChildProgresses,
    childErrors: mergeResult.nextChildErrors,
  };
}

/**
 * Merge one child's progress into a coalesced parent row in an immutable `progressStatus` map.
 * Used by FromLibraryTab HTTP fan-out; mirrors WS coalescing field preservation.
 *
 * @param {Record<string, unknown>} prev - Full previous `progressStatus`
 * @param {object} opts
 * @param {string} opts.parentKey
 * @param {number} opts.childCount
 * @param {string} opts.childCloneName
 * @param {number} opts.childProgress
 * @param {boolean} [opts.childIsError]
 * @param {(existing: Record<string, unknown>) => Record<string, unknown>} opts.preserve - fields merged after patch
 */
export function applyChildProgressToCoalescedParentMap(prev, {
  parentKey,
  childCount,
  childCloneName,
  childProgress,
  childIsError = false,
  preserve,
}) {
  const existing = prev[parentKey] || {};
  const merged = mergeCoalescedGroupChildIntoParent(
    existing,
    { childCloneName, childProgress, childIsError },
    childCount
  );
  const patched = applyCoalescedProgressPatchToParent(existing, merged);
  const extra = typeof preserve === 'function' ? preserve(existing) : {};
  return {
    ...prev,
    [parentKey]: {
      ...patched,
      ...extra,
    },
  };
}

/**
 * Active plain-upload rows (file upload / library clone / HF import) from
 * `progressStatus` for one task — the upload analogue of
 * `listActiveAiGenerationEntriesForUsecase` / `listActiveRetrievalAgentEntriesForUsecase`.
 * Matches by `usecaseId` (or `affectedUsecaseIds` for composed-task groups) and keeps
 * only non-terminal entries (phases `uploading` / `processing` / `label_generation`).
 */
export function listActiveUploadEntriesForUsecase(progressStatus, usecaseId) {
  if (!progressStatus || !usecaseId) return [];
  const wanted = String(usecaseId);
  return Object.entries(progressStatus)
    .filter(([, value]) => {
      if (!value) return false;
      if (value.isDownload || value.isGeneration || value.isRetrievalAgent) return false;
      if (value.isCompleted || value.isError) return false;
      if (value.phase === 'completed') return false;
      const progress = typeof value.progress === 'number' ? value.progress : 0;
      if (progress >= 100) return false;
      const matchesPrimary = value.usecaseId != null && String(value.usecaseId) === wanted;
      const matchesAffected = Array.isArray(value.affectedUsecaseIds)
        && value.affectedUsecaseIds.some((id) => String(id) === wanted);
      return matchesPrimary || matchesAffected;
    })
    .map(([progressKey, value]) => ({ ...value, progressKey }));
}

export function initTwoPhaseProgressItem({
  setProgressStatus,
  key,
  datasetName,
  usecaseId,
  message = 'Uploading file',
  /** True if this dataset/benchmark name was already on the usecase before this upload (append flow). */
  existedOnUsecaseBeforeUpload = false,
  /** True for AI-labeled uploads that will trigger label generation after processing. */
  isLabelGeneration = false,
  /** Creation wizard: wait for Model Config before label_generation phase. */
  deferLabelGeneration = false,
  /** True when this card is a benchmark (vs train dataset). */
  isBenchmark = false,
  /** All composed-task usecase ids affected by this grouped upload (Data Editor loading on every tab). */
  affectedUsecaseIds = null,
}) {
  if (typeof setProgressStatus !== 'function' || !key) return;

  setProgressStatus((prev) => ({
    ...prev,
    [key]: {
      ...(prev?.[key] || {}),
      // Keep slightly > 0 so the floating progress bar appears immediately.
      progress: 0.1,
      uploadProgress: 0,
      processingProgress: 0,
      phase: 'uploading',
      isCompleted: false,
      isError: false,
      datasetName: datasetName || key,
      usecaseId,
      isBenchmark: Boolean(isBenchmark),
      ...(Array.isArray(affectedUsecaseIds) && affectedUsecaseIds.length > 0
        ? { affectedUsecaseIds: [...affectedUsecaseIds] }
        : {}),
      message,
      details: null,
      existedOnUsecaseBeforeUpload: Boolean(existedOnUsecaseBeforeUpload),
      isLabelGeneration: Boolean(isLabelGeneration),
      deferLabelGeneration: Boolean(deferLabelGeneration),
    },
  }));
}

export function updateTwoPhaseUploadProgress({ setProgressStatus, key, uploadPercent }) {
  if (typeof setProgressStatus !== 'function' || !key) return;
  if (uploadPercent == null) return;

  const clamped = Math.max(0, Math.min(100, uploadPercent));
  const overall = Math.max(0.1, Math.min(50, (clamped / 100) * 50));

  setProgressStatus((prev) => {
    const existing = prev?.[key] || {};
    return {
      ...prev,
      [key]: {
        ...existing,
        progress: overall,
        uploadProgress: clamped,
        phase: 'uploading',
        isCompleted: false,
        isError: false,
        message: `Uploading ${Math.round(clamped)}%`,
      },
    };
  });
}

export function markTwoPhaseUploadCompleteStartProcessing({ setProgressStatus, key }) {
  if (typeof setProgressStatus !== 'function' || !key) return;

  setProgressStatus((prev) => {
    const existing = prev?.[key] || {};
    return {
      ...prev,
      [key]: {
        ...existing,
        progress: Math.max(existing.progress || 0, 50),
        uploadProgress: 100,
        phase: 'processing',
        message: 'Processing file',
      },
    };
  });
}

export function initCloneProgressItem({
  setProgressStatus,
  key,
  datasetName,
  usecaseId,
  clientId,
  isBenchmark = false,
  message = 'Cloning dataset',
  affectedUsecaseIds = null,
  /** True for AI-labeled clones that trigger label generation after copy. */
  isLabelGeneration = false,
  /** Creation wizard: wait for Model Config before label_generation phase. */
  deferLabelGeneration = false,
}) {
  if (typeof setProgressStatus !== 'function' || !key) return;

  setProgressStatus((prev) => ({
    ...prev,
    [key]: {
      ...(prev?.[key] || {}),
      progress: 0.1,
      uploadProgress: 0,
      processingProgress: 0,
      phase: 'processing',
      isCompleted: false,
      isError: false,
      datasetName: datasetName || key,
      usecaseId,
      clientId,
      isBenchmark,
      ...(Array.isArray(affectedUsecaseIds) && affectedUsecaseIds.length > 0
        ? { affectedUsecaseIds: [...affectedUsecaseIds] }
        : {}),
      message,
      details: null,
      existedOnUsecaseBeforeUpload: false,
      isLabelGeneration: Boolean(isLabelGeneration),
      deferLabelGeneration: Boolean(deferLabelGeneration),
    },
  }));
}

export function markTwoPhaseFailed({ setProgressStatus, key, errorMessage, details }) {
  if (typeof setProgressStatus !== 'function' || !key) return;

  const msg = formatApiErrorDetail(errorMessage);
  const detailText = details != null && details !== '' ? formatApiErrorDetail(details) : '';
  setProgressStatus((prev) => {
    const existing = prev?.[key] || {};
    return {
      ...prev,
      [key]: {
        ...existing,
        isError: true,
        message: msg ? `Upload failed: ${msg}` : 'Upload failed',
        details: detailText || msg || existing.details,
      },
    };
  });
}

export function findLabelGenerationProgressKey(progressStatus, { usecaseId, datasetName, excludeKey } = {}) {
  const keys = Object.keys(progressStatus || {}).filter((k) => {
    if (excludeKey && k === excludeKey) return false;
    return Boolean(progressStatus[k]?.isLabelGeneration);
  });
  const name = datasetName || '';
  if (name) {
    const byName = keys.find((k) => k === name || progressStatus[k]?.datasetName === name);
    if (byName) return byName;
  }
  if (!usecaseId) return null;
  const byUsecase = keys.filter((k) => progressStatus[k]?.usecaseId === usecaseId);
  return byUsecase.length === 1 ? byUsecase[0] : null;
}

export function applyArmLabelGenerationToProgress(prev, {
  usecaseId,
  startedDatasets = [],
  pendingIngestDatasets = [],
  alreadyTriggeredDatasets = [],
  skippedNoUnlabeledDatasets = [],
} = {}) {
  if (!prev || !usecaseId) return prev;
  const started = new Set([
    ...(startedDatasets || []),
    ...(alreadyTriggeredDatasets || []),
  ]);
  const pending = new Set(pendingIngestDatasets || []);
  const noUnlabeled = new Set(skippedNoUnlabeledDatasets || []);
  let changed = false;
  const next = { ...prev };
  for (const key of Object.keys(next)) {
    const item = next[key];
    if (!item?.isLabelGeneration || item.usecaseId !== usecaseId) continue;
    changed = true;
    if (item.isError) {
      next[key] = { ...item, labelGenerationArmed: true };
      continue;
    }
    const name = item.datasetName || key;
    const processingDone =
      item.phase === 'label_generation' ||
      item.phase === 'completed' ||
      (typeof item.processingProgress === 'number' && item.processingProgress >= 100);
    if (noUnlabeled.has(name) || noUnlabeled.has(key)) {
      next[key] = {
        ...item,
        isLabelGeneration: false,
        deferLabelGeneration: false,
        labelGenerationArmed: true,
        phase: processingDone ? 'completed' : item.phase,
        isCompleted: processingDone,
        progress: processingDone ? 100 : item.progress,
        message: processingDone ? 'Processing complete' : item.message,
      };
      continue;
    }
    const startNow =
      started.has(name) ||
      started.has(key) ||
      (processingDone && !pending.has(name) && !pending.has(key));
    if (startNow) {
      next[key] = {
        ...item,
        labelGenerationArmed: true,
        deferLabelGeneration: false,
        phase: 'label_generation',
        isCompleted: false,
        isError: false,
        progress: Math.max(item.progress || 0, 99),
        uploadProgress: Math.max(item.uploadProgress || 0, 100),
        processingProgress: Math.max(item.processingProgress || 0, 100),
        message: 'Generating labels',
      };
    } else {
      next[key] = {
        ...item,
        labelGenerationArmed: true,
        message: 'Waiting for processing to finish',
      };
    }
  }
  return changed ? next : prev;
}

export function foldGenerationProgressIntoUploadItem(existing, {
  isFailed,
  isCompleted,
  pct,
  errorDetails,
  warningFields = {},
  processedPrompts = 0,
  totalPrompts = 0,
  phase,
} = {}) {
  const processingLooksDone =
    existing?.phase === 'label_generation' ||
    existing?.phase === 'completed' ||
    (typeof existing?.processingProgress === 'number' && existing.processingProgress >= 100);
  if (isFailed && !processingLooksDone) {
    return existing;
  }
  // LABEL_ONLY no-op: every row already had a label. Drop the Generating
  // labels step instead of showing a 0/0 completed run.
  if (isCompleted && !isFailed && processedPrompts === 0 && totalPrompts === 0) {
    return {
      ...existing,
      isLabelGeneration: false,
      deferLabelGeneration: false,
      labelGenerationArmed: true,
      phase: processingLooksDone ? 'completed' : existing?.phase,
      isCompleted: Boolean(processingLooksDone),
      isError: false,
      progress: processingLooksDone ? 100 : existing?.progress,
      uploadProgress: Math.max(existing?.uploadProgress || 0, 100),
      processingProgress: Math.max(existing?.processingProgress || 0, 100),
      message: processingLooksDone ? 'Processing complete' : existing?.message,
      labelGenerationProgress: 100,
    };
  }
  const inProgressPhase =
    !isCompleted && !isFailed
      ? {
          phase: 'label_generation',
          isCompleted: false,
          progress: Math.max(existing?.progress || 0, 99),
          message: 'Generating labels',
          deferLabelGeneration: false,
          labelGenerationArmed: true,
        }
      : {};
  // Raw generation phase (e.g. ``batch_processing``) and the labeled / total
  // counts feed the "Generating labels" row subtitle. Keep the last known
  // phase when a payload has none (older producers).
  const jobPhase = normalizeJobPhase(phase) || existing?.jobPhase;
  return {
    ...existing,
    labelGenerationProgress: pct,
    ...(jobPhase ? { jobPhase } : {}),
    processedPrompts,
    totalPrompts,
    ...inProgressPhase,
    ...(isCompleted
      ? {
          phase: 'completed',
          isCompleted: true,
          progress: 100,
          isError: false,
          deferLabelGeneration: false,
          labelGenerationArmed: true,
        }
      : {}),
    ...(isFailed
      ? {
          phase: 'label_generation',
          isError: true,
          isCompleted: false,
          deferLabelGeneration: false,
          labelGenerationArmed: true,
          message: errorDetails || 'Label generation failed',
          details: errorDetails || existing?.details,
          uploadProgress: Math.max(existing?.uploadProgress || 0, 100),
          processingProgress: Math.max(existing?.processingProgress || 0, 100),
        }
      : {}),
    ...warningFields,
  };
}

